// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * University AI settings types and validators (**ADR-0080**).
 *
 * **No type here can carry a provider secret.** The credential view has members
 * for state, version, a last four, timestamps and a bounded validation code, and
 * nothing else — the same shape Platform publishes. A screen cannot render a key
 * it has no field to receive, which is a stronger guarantee than remembering not
 * to display one.
 *
 * The API key appears in exactly one direction: `RotateCredentialInput`, on its
 * way to the BFF. It is never part of a response type.
 */

import { z } from 'zod';

/** Provider types this build can configure. */
export const providerTypes = ['anthropic', 'openai', 'google', 'xai'] as const;

/** One configurable provider type. */
export type ProviderType = (typeof providerTypes)[number];

/** Human labels, so the selector reads as an administrator expects. */
export const providerLabels: Readonly<Record<ProviderType, string>> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google Gemini',
  xai: 'xAI Grok',
};

/**
 * Which non-secret fields each provider actually uses.
 *
 * Drives the form so an administrator is not asked for an OpenAI organization
 * while configuring Anthropic. Adding a provider adds an entry here; it does not
 * change the form.
 */
export const providerFields: Readonly<Record<ProviderType, readonly string[]>> = {
  anthropic: [],
  openai: ['organization', 'project'],
  google: [],
  xai: [],
};

export const credentialViewSchema = z.object({
  state: z.enum(['pending_validation', 'active', 'superseded', 'invalid', 'revoked']),
  version: z.number().int().min(1),
  key_tail: z.string().max(8),
  created_at: z.string(),
  activated_at: z.string().nullable().optional(),
  last_validated_at: z.string().nullable().optional(),
  last_validation_status: z.enum(['succeeded', 'failed']).nullable().optional(),
  last_validation_error_code: z.string().nullable().optional(),
});

export const providerViewSchema = z.object({
  provider_id: z.string(),
  display_name: z.string(),
  provider_type: z.string(),
  enabled: z.boolean(),
  config: z.object({
    endpoint: z.string().nullable().optional(),
    region: z.string().nullable().optional(),
    project_id: z.string().nullable().optional(),
    deployment: z.string().nullable().optional(),
  }),
  credential: credentialViewSchema.nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const modelViewSchema = z.object({
  model_id: z.string(),
  provider_id: z.string(),
  display_name: z.string(),
  provider_model_ref: z.string(),
  enabled: z.boolean(),
  validated: z.boolean(),
  context_window: z.number().int().nullable().optional(),
  request_timeout_ms: z.number().int(),
  max_output_tokens: z.number().int().nullable().optional(),
  capabilities: z.array(z.string()).default([]),
  created_at: z.string(),
  updated_at: z.string(),
});

export const routingViewSchema = z.object({
  configuration_id: z.string().nullable().optional(),
  version: z.number().int().nullable().optional(),
  entries: z
    .array(z.object({ position: z.number().int().min(0), model_id: z.string() }))
    .default([]),
  activated_at: z.string().nullable().optional(),
});

export const providerListSchema = z.array(providerViewSchema);
export const modelListSchema = z.array(modelViewSchema);

export type CredentialView = z.infer<typeof credentialViewSchema>;
export type ProviderView = z.infer<typeof providerViewSchema>;
export type ModelView = z.infer<typeof modelViewSchema>;
export type RoutingView = z.infer<typeof routingViewSchema>;

/** Register a provider. */
export interface CreateProviderInput {
  readonly display_name: string;
  readonly provider_type: ProviderType;
  readonly enabled?: boolean;
  readonly config?: Readonly<Record<string, string>>;
  readonly signal?: AbortSignal | undefined;
}

/** Amend a provider. Omitted members are left alone. */
export interface AmendProviderInput {
  readonly provider_id: string;
  readonly display_name?: string;
  readonly enabled?: boolean;
  readonly signal?: AbortSignal | undefined;
}

/**
 * Store or rotate a provider's API key.
 *
 * The only type in this module that carries a secret, and it travels one way.
 */
export interface RotateCredentialInput {
  readonly provider_id: string;
  readonly api_key: string;
  readonly signal?: AbortSignal | undefined;
}

/** Register a model. */
export interface CreateModelInput {
  readonly provider_id: string;
  readonly display_name: string;
  readonly provider_model_ref: string;
  readonly enabled?: boolean;
  readonly signal?: AbortSignal | undefined;
}

/** Amend a model. */
export interface AmendModelInput {
  readonly model_id: string;
  readonly display_name?: string;
  readonly enabled?: boolean;
  readonly signal?: AbortSignal | undefined;
}

/** Activate a routing chain. Position zero is the primary. */
export interface ActivateRoutingInput {
  readonly entries: readonly { readonly position: number; readonly model_id: string }[];
  readonly expected_version?: number | undefined;
  readonly signal?: AbortSignal | undefined;
}

/** The AI settings surface, as the Control Center consumes it. */
export interface AiSettingsClient {
  listProviders(signal?: AbortSignal): Promise<readonly ProviderView[]>;
  createProvider(input: CreateProviderInput): Promise<ProviderView>;
  amendProvider(input: AmendProviderInput): Promise<ProviderView>;
  deleteProvider(providerId: string, signal?: AbortSignal): Promise<void>;
  rotateCredential(input: RotateCredentialInput): Promise<ProviderView>;
  testProvider(providerId: string, signal?: AbortSignal): Promise<ProviderView>;
  listModels(signal?: AbortSignal): Promise<readonly ModelView[]>;
  createModel(input: CreateModelInput): Promise<ModelView>;
  amendModel(input: AmendModelInput): Promise<ModelView>;
  deleteModel(modelId: string, signal?: AbortSignal): Promise<void>;
  readRouting(signal?: AbortSignal): Promise<RoutingView>;
  activateRouting(input: ActivateRoutingInput): Promise<RoutingView>;
}
